import { useCallback } from 'react';
import { can, canAll, type CanScope } from './permissions';
import { sessionStore, type SessionState } from './session-store';
import { type PermissionGrant } from './types';

/** Whole session state (re-renders on any change). */
export function useSession(): SessionState {
  return sessionStore();
}

/**
 * `useCan('inventory:balance:adjust', { warehouseId })`. An array requires every permission.
 * Reads the grants from the session store; deny by default while the session is not loaded.
 */
export function useCan(permission: string | string[], scope?: CanScope): boolean {
  const grants = sessionStore((s) => s.permissions);
  return Array.isArray(permission) ? canAll(grants, permission, scope) : can(grants, permission, scope);
}

export interface UsePermissionsResult {
  can: (permission: string, scope?: CanScope) => boolean;
  grants: PermissionGrant[];
}

/** Imperative permission checker bound to the current grants. */
export function usePermissions(): UsePermissionsResult {
  const grants = sessionStore((s) => s.permissions);
  const check = useCallback((permission: string, scope?: CanScope) => can(grants, permission, scope), [grants]);
  return { can: check, grants };
}
